import {
  addUserAddress, updateUserAddress, removeUserAddress, userAddresses,
  addMemberAddress, updateMemberAddress, removeMemberAddress, memberAddresses,
  addTeamAddress, updateTeamAddress, removeTeamAddress, teamAddresses,
  getCountries,
} from "@/services/addresses";
import combineAddressLists from '@/lib/combineAddressLists';

export default {
  state: {
    userAddresses: [],
    teamAddresses: null,
    memberAddresses: null,
    countries: [],
  },
  mutations: {
    setUserAddresses(state, addrs) {
      state.userAddresses = addrs;
    },
    setTeamAddresses(state, addrs) {
      state.teamAddresses = addrs;
    },
    resetTeamAddresses(state) {
      state.teamAddresses = null;
    },
    setCountries(state, countries) {
      // Show only "shipping countries" in the list.
      const shippingCountries = countries.filter(country => country.is_shipping_country);
      // Extend each country with country code.
      const extendedCountries = shippingCountries.map(country => {
        let match;
        match = /countries\/(..)\//.exec(country.url);
        // Key `iso_3166_1_a2` used because this is a name of field
        // that keeps country code on backend.
        return {...country, iso_3166_1_a2: match[1]}
      })

      // Set "USA" as the first country in the list.
      state.countries = [
        ...extendedCountries.filter(c => c.iso_3166_1_a3 === "USA"),
        ...extendedCountries.filter(c => c.iso_3166_1_a3 !== "USA"),
      ]
    },
    setMemberAddresses(state, addrs) {
      state.memberAddresses = addrs;
    }
  },
  actions: {
    // User addresses
    async addUserAddress({dispatch}, address) {
      await addUserAddress(address);
      await dispatch('refreshUserAddresses');
    },
    async updateUserAddress({dispatch}, address) {
      await updateUserAddress(address);
      await dispatch('refreshUserAddresses');
    },
    async removeUserAddress({dispatch}, address) {
      await removeUserAddress(address);
      await dispatch('refreshUserAddresses');
    },
    async loadUserAddresses({ dispatch, getters }) {
      const addrs = getters.userAddresses;
      if (addrs && addrs.length > 0) return;
      await dispatch('refreshUserAddresses');
    },
    async refreshUserAddresses({commit}) {
      let addrs = await userAddresses();
      commit('setUserAddresses', addrs);
    },
    // Member addresses
    async addMemberAddress({getters, dispatch}, address) {
      await addMemberAddress(getters.member.id, address);
      await dispatch('refreshMemberAddresses');
    },
    async updateMemberAddress({getters, dispatch}, address) {
      await updateMemberAddress(getters.member.id, address);
      await dispatch('refreshMemberAddresses');
    },
    async removeMemberAddress({getters, dispatch}, address) {
      await removeMemberAddress(getters.member.id, address);
      await dispatch('refreshMemberAddresses');
    },
    async refreshMemberAddresses({getters, commit}) {
      let addrs = getters.member.id ? await memberAddresses(getters.member.id) : [];
      commit('setMemberAddresses', addrs);
    },
    // Team addresses
    async addTeamAddress({dispatch}, address) {
      let {id} = await addTeamAddress(address);
      await dispatch('refreshTeamAddresses');
      return id;
    },
    async updateTeamAddress({dispatch}, address) {
      await updateTeamAddress(address);
      await dispatch('refreshTeamAddresses');
    },
    async removeTeamAddress({dispatch}, address) {
      await removeTeamAddress(address);
      await dispatch('refreshTeamAddresses');
    },
    async loadTeamAddresses({getters, dispatch}) {
      const addrs = getters.teamAddresses;
      if (addrs && addrs.length > 0) return;
      dispatch('refreshTeamAddresses');
    },
    async refreshTeamAddresses({commit}) {
      let addrs = await teamAddresses();
      commit('setTeamAddresses', addrs);
    },
    // Combined user/team addresses
    // These work with the resuls from combinedAddresses
    async addAddress({dispatch}, address) {
      return doCombinedAction(dispatch, address, 'add');
    },
    async updateAddress({dispatch}, address) {
      let {origType, type} = address;
      if (origType && origType !== type) {
        // changing type -- delete then add
        return Promise.all([
          doCombinedAction(dispatch, {...address, type: origType}, 'remove'),
          doCombinedAction(dispatch, address, 'add')
        ]);
      } else {
        return doCombinedAction(dispatch, address, 'update');
      }
    },
    async removeAddress({dispatch}, address) {
      return doCombinedAction(dispatch, address, 'remove');
    },
    async refreshAddresses({dispatch}) {
      return Promise.all([
        dispatch('refreshTeamAddresses'),
        dispatch('refreshUserAddresses'),
        dispatch('refreshMemberAddresses'),
      ]);
    },
    async setDefaultUserAddress({dispatch}, address) {
      // The default_team_address on the user profile takes precendence.
      // The API handles making one only user address have is_default_for_shipping.
      // Current favorite -> new favorite : how it works
      //  team -> team : update default_team_address
      //  team -> user : set default_team_address to null, set address is_default_for_shipping
      //  user -> team : update default_team_address. That shadows the user address.
      //  none -> team : update default_team_address
      //  none -> user : set address is_default_for_shipping
      //  user -> user : set address is_default_for_shipping. API unsets for other addresses.
      if (address.type === 'team') {
        return dispatch('updateProfile', {default_team_address: address.id});
      } else {
        return Promise.all([
          dispatch('updateProfile', {default_team_address: null}),
          dispatch('updateAddress', {...address, is_default_for_shipping: true})
        ]);
      }
    },
    async loadCountries({commit}) {
      const {results} = await getCountries();
      commit('setCountries', results)
    },
    async loadMemberAddresses({commit, getters}, memberId) {
      // Load the addresses for the current team member.
      // Ensure there is minimal time where we have the addresses for the wrong member.
      commit('setMemberAddresses', null);
      commit('setMemberAddresses', await memberAddresses(memberId));
    },
    async setDefaultMemberAddress({getters, dispatch}, address) {
      // See setDefaultUserAddress for details.
      let id = getters.member.id;
      if (address.type === 'team') {
        return dispatch('updateUserProfile', {id, default_team_address: address.id});
      } else {
        return Promise.all([
          dispatch('updateUserProfile', {id, default_team_address: null}),
          dispatch('updateMemberAddress', {...address, is_default_for_shipping: true})
        ]);
      }
    },
  },
  getters: {
    userAddresses: state => state.userAddresses || [],
    teamAddresses: state => state.teamAddresses,
    combinedAddresses: (state, getters) =>
      getters.user
      ? combineAddressLists(
          state.teamAddresses,
          getters.userAddresses,
          getters.user.default_team_address
        )
      : null,
    combinedMemberAddresses: (state, getters) =>
      getters.member
      ? combineAddressLists(
          state.teamAddresses,
          state.memberAddresses,
          getters.member.default_team_address,
          'member'
        )
      : null,
    countries: state => state.countries,
    defaultMemberAddress: (state, getters) => {
      if (!getters.member) return;
      // Caller should ensure the team addresses are loaded
      let {default_team_address} = getters.member;
      // Return team address specified by the address_id, or
      // the personal address has the is_default_for_shipping,
      if (default_team_address) {
        if (!state.teamAddresses) return null; // Team addresses not loaded yet.
        return state.teamAddresses.find(addr => default_team_address == addr.id);
      } else {
        if (!state.memberAddresses) return null; // Unexpected, but to be safe
        return state.memberAddresses.find(addr => addr.is_default_for_shipping);
      }
    },
  }
}

function doCombinedAction(dispatch, address, actionPrefix) {
  let type = address.type;
  console.assert(type === 'user' || type === 'team' || type == 'member');
  let camelType = type[0].toUpperCase() + type.substring(1);
  let action = actionPrefix + camelType + 'Address';
  return dispatch(action, address);
}



// WEBPACK FOOTER //
// ./src/store/addresses.js